import {
  CardFooterButton,
  ModalCard,
  SelectField,
  SimpleForm,
  SimpleFormField,
  type Toggle,
  useMessages,
} from '@appsemble/react-components';
import { type OrganizationSubscription, SubscriptionRenewalPeriod } from '@appsemble/types';
import axios from 'axios';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { messages } from './messages.js';

interface ExtendOrganizationSubscriptionModalProps {
  /**
   * The state of the modal.
   */
  readonly state: Toggle;

  /**
   * The subscription user wants to cancel.
   */
  readonly subscription: OrganizationSubscription;
}

export function ExtendOrganizationSubscriptionModal({
  state,
  subscription,
}: ExtendOrganizationSubscriptionModalProps): ReactNode {
  const [submitting, setSubmitting] = useState(false);
  const push = useMessages();
  const { formatMessage } = useIntl();

  const defaultValues = useMemo(
    () => ({
      cancelled: false,
      renewalPeriod: subscription?.renewalPeriod || SubscriptionRenewalPeriod.Month,
    }),
    [subscription],
  );

  const onSubmit = useCallback(
    async ({ renewalPeriod }: typeof defaultValues) => {
      setSubmitting(true);
      const formData = new FormData();
      formData.set('cancelled', 'false');
      formData.set('renewalPeriod', renewalPeriod);
      try {
        await axios.patch(`/api/organization-subscriptions/${subscription.id}`, formData);
      } catch {
        setSubmitting(false);
        push(formatMessage(messages.error));
        return;
      }
      state.disable();
      setSubmitting(false);
    },
    [formatMessage, push, state, subscription],
  );

  return (
    <ModalCard
      component={SimpleForm}
      defaultValues={defaultValues}
      footer={
        <>
          <CardFooterButton onClick={state.toggle}>keep subscription</CardFooterButton>
          <CardFooterButton color="primary" type="submit">
            <FormattedMessage {...messages.extendSubscription} />
          </CardFooterButton>
        </>
      }
      isActive={state.enabled}
      onClose={state.disable}
      onSubmit={onSubmit}
      title={<FormattedMessage {...messages.title} />}
    >
      <SimpleFormField
        component={SelectField}
        disabled={submitting}
        help={<FormattedMessage {...messages.billingPeriodHelp} />}
        icon="info"
        label={<FormattedMessage {...messages.billingPeriod} />}
        name="renewalPeriod"
        required
      >
        <option value={SubscriptionRenewalPeriod.Month}>
          <FormattedMessage {...messages.month} />
        </option>
        <option value={SubscriptionRenewalPeriod.Year}>
          <FormattedMessage {...messages.year} />
        </option>
      </SimpleFormField>
    </ModalCard>
  );
}
